'use client';

import { LocaleLink } from '@/components/locale-link';
import { trackSearchResultClick } from '@/lib/analytics';
import { LegalStatusBadge } from './LegalStatusBadge';
import type { CellContext } from '@tanstack/react-table';
import type { CompanySearchResult } from '@/lib/actions/company-search';

interface CompanyNameLinkProps {
  slug: string;
  results: CompanySearchResult[];
  company: CompanySearchResult;
  offset: number;
  onOpenPreview: ((euId: string, trigger?: HTMLElement) => void) | undefined;
  faviconUrl: string;
  info: CellContext<CompanySearchResult, unknown>;
}

export function CompanyNameLink({
  slug,
  results,
  company,
  offset,
  onOpenPreview,
  faviconUrl,
  info,
}: CompanyNameLinkProps) {
  return (
<LocaleLink
              href={slug}
              className="flex items-center gap-2"
              onClick={(e) => {
                const position = results.findIndex((r) => r.euId === company.euId);
                trackSearchResultClick(company.euId, offset + position);
                e.stopPropagation();
                if (
                  onOpenPreview && e.button === 0 &&
                  !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey
                ) {
                  e.preventDefault();
                  onOpenPreview(company.euId, e.currentTarget);
                }
              }}
              onAuxClick={(e) => e.stopPropagation()}
            >
              {faviconUrl && (
                <span className="flex h-5 w-5 items-center justify-center rounded-sm bg-white/95 dark:bg-white border border-black/5 dark:border-white/20 shrink-0">
                  <img
                    src={faviconUrl}
                    alt={`${info.getValue() as string} favicon`}
                    className="h-3.5 w-3.5 object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </span>
              )}
              <span
                className="font-medium text-sm text-foreground truncate block max-w-[180px] lg:max-w-[300px] hover:text-primary hover:underline transition-colors"
                title={info.getValue() as string}
              >
                {info.getValue() as string}
              </span>
              {/* Shown inline rather than only as a toggleable column: a
                  company being insolvent changes what the row means, and
                  users should not have to enable a column to find that out. */}
              <LegalStatusBadge status={company.legalStatus} />
            </LocaleLink>
  );
}
